import { useCallback, useEffect, useRef, useState } from "react";
import useTranslation from "../../../hooks/i18n/useTranslation";
import IconsEnum from "../../../classes/enums/IconsEnum";
import ToolPopUp from "../../../components/toolPopUp/ToolPopUp";
import measurementToolManager from "../../../services/MeasurementToolService";
import AddLandmark from "../../validation/landmarkManagementPopups/addLandmarkPopup/AddLandmark";
import RemoveLandmark from "../../validation/landmarkManagementPopups/removeLandmarkPopup/RemoveLandmark";
import AddVertebra from "../../validation/vertebraManagementPopups/addVertebraPopup/AddVertebra";
import RemoveVertebra from "../../validation/vertebraManagementPopups/removeVertebraPopup/RemoveVertebra";
import "./ToolBar.css";
import ToolToggle from "./ToolToggle";

import { ROUTE_TYPES } from "../../../classes/constants/routeConstants";
import { IconSize } from "../../../classes/enums/ButtonEnum";
import {
  InteractiveTools,
  POPUP_CONTENT_TYPES,
  POPUP_TYPES,
  TOOLS_TEXT,
  ToolShape,
} from "../../../classes/enums/ToolsEnum";
import { MacroRegions } from "../../../classes/enums/ValidationEnums";

const DistanceTools = ({ selectedTool, onToolSelect }) => (
  <div className="tool-grid">
    <ToolToggle
      icon={IconsEnum.DISTANCEGENERIC}
      onClick={() => onToolSelect(InteractiveTools.DISTANCE_GENERIC)}
      isSelected={selectedTool === InteractiveTools.DISTANCE_GENERIC}
      noBgColor
      title="Generic"
    />
    <ToolToggle
      icon={IconsEnum.DISTANCEHORIZONTAL}
      onClick={() => onToolSelect(InteractiveTools.DISTANCE_HORIZONTAL)}
      isSelected={selectedTool === InteractiveTools.DISTANCE_HORIZONTAL}
      noBgColor
      title="Horizontal"
    />
    <ToolToggle
      icon={IconsEnum.DISTANCEVERTICAL}
      onClick={() => onToolSelect(InteractiveTools.DISTANCE_VERTICAL)}
      isSelected={selectedTool === InteractiveTools.DISTANCE_VERTICAL}
      noBgColor
      title="Vertical"
    />
  </div>
);

const AngleTools = ({ selectedTool, onToolSelect }) => (
  <div className="tool-grid angle-grid">
    <ToolToggle
      icon={IconsEnum.ANGLEHORIZONTAL}
      onClick={() => onToolSelect(InteractiveTools.ANGLE_HORIZONTAL)}
      isSelected={selectedTool === InteractiveTools.ANGLE_HORIZONTAL}
      noBgColor
      title="Horizontal"
    />
    <ToolToggle
      icon={IconsEnum.ANGLEVERTICAL}
      onClick={() => onToolSelect(InteractiveTools.ANGLE_VERTICAL)}
      isSelected={selectedTool === InteractiveTools.ANGLE_VERTICAL}
      noBgColor
      title="Vertical"
    />
    <ToolToggle
      icon={IconsEnum.ANGLEINNER}
      onClick={() => onToolSelect(InteractiveTools.ANGLE_INNER)}
      isSelected={selectedTool === InteractiveTools.ANGLE_INNER}
      noBgColor
      title="Inner"
    />
    <ToolToggle
      icon={IconsEnum.ANGLE4POINTS}
      onClick={() => onToolSelect(InteractiveTools.ANGLE_4_POINTS)}
      isSelected={selectedTool === InteractiveTools.ANGLE_4_POINTS}
      noBgColor
      title="4 Points"
    />
    <ToolToggle
      icon={IconsEnum.ANGLECOMP}
      onClick={() => onToolSelect(InteractiveTools.ANGLE_COMP)}
      isSelected={selectedTool === InteractiveTools.ANGLE_COMP}
      noBgColor
      title="Comp"
    />
  </div>
);

const PredefinedTools = ({ selectedTool, onToolSelect }) => (
  <div className="advanced-angle-grid">
    <ToolToggle
      icon={IconsEnum.ANGLEKYPHOSIS}
      iconSize={IconSize.MEDIUM}
      onClick={() => onToolSelect(InteractiveTools.ANGLE_KYPHOSIS)}
      isSelected={selectedTool === InteractiveTools.ANGLE_KYPHOSIS}
      noBgColor
      title="Kyphosis-Lordosis"
    />
    <ToolToggle
      icon={IconsEnum.ANGLECOBB}
      iconSize={IconSize.MEDIUM}
      onClick={() => onToolSelect(InteractiveTools.ANGLE_COBB)}
      isSelected={selectedTool === InteractiveTools.ANGLE_COBB}
      noBgColor
      title="Cobb"
    />
  </div>
);

function ToolBar({
  step = ROUTE_TYPES.CHECK,
  onSurgeryToolSelect = null,
  selectedSurgeryTool = null,
  currentValidationRegion = "spine", // "spine" or "lower_body"
  checkToolsState = {
    threeDView: {
      has3dInstances: false,
      is3dViewActive: false,
      onView3dToggle: null,
    },
    radioDensity: {
      hasRadioDensity: false,
      isRadioDensityActive: false,
      onRadioDensityToggle: null,
    },
    measurementsState: {
      canMeasure: true,
    },
  },
  alignToolsState = {
    globalAlignment: false,
    osteotomy: false,
    segmentalTransformation: false,
    cages: false,
    fusion: false,
  },
  sharedToolsState = {
    dataFusion: {
      hasDataFusion: false,
      isDataFusionActive: false,
      onDataFusionToggle: null,
    },
  },
  disabled = false,
}) {
  const { t } = useTranslation();
  const [activeSubToolPopup, setActiveSubToolPopup] = useState(null);
  const [selectedTool, setSelectedTool] = useState(null);

  const popupRef = useRef(null);

  const [popupPosition, setPopupPosition] = useState({ top: 0, left: 0 });

  const distanceButtonRef = useRef(null);
  const angleButtonRef = useRef(null);
  const advancedButtonRef = useRef(null);
  const addVertebraButtonRef = useRef(null);
  const removeVertebraButtonRef = useRef(null);
  const addLandmarkButtonRef = useRef(null);
  const removeLandmarkButtonRef = useRef(null);

  const openPopup = (popupType, buttonRef) => {
    if (buttonRef?.current) {
      const buttonRect = buttonRef.current.getBoundingClientRect();
      const popupWidth = 300;

      setPopupPosition({
        top: buttonRect.top + buttonRect.height / 2,
        left: buttonRect.left - popupWidth - 10,
      });
    }
    setActiveSubToolPopup(popupType);
  };

  const closePopup = useCallback(() => {
    setActiveSubToolPopup(null);
  }, []);

  const handleToolSelected = useCallback(async toolName => {
    const measurementTools = [
      InteractiveTools.DISTANCE_GENERIC,
      InteractiveTools.DISTANCE_HORIZONTAL,
      InteractiveTools.DISTANCE_VERTICAL,
      InteractiveTools.ANGLE_HORIZONTAL,
      InteractiveTools.ANGLE_VERTICAL,
      InteractiveTools.ANGLE_INNER,
      InteractiveTools.ANGLE_4_POINTS,
      InteractiveTools.ANGLE_COMP,
      InteractiveTools.ANGLE_COBB,
      InteractiveTools.ANGLE_KYPHOSIS,
      InteractiveTools.ANGLE_LORDOSIS,
    ];

    if (measurementTools.includes(toolName)) {
      setSelectedTool(toolName);
      measurementToolManager.selectTool(toolName);
    } else {
    }
  }, []);

  const handleSubToolSelect = useCallback(
    async toolName => {
      await handleToolSelected(toolName);
      closePopup();
    },
    [closePopup, handleToolSelected]
  );

  const handleSurgeryToolClick = useCallback(
    toolType => {
      if (onSurgeryToolSelect) {
        onSurgeryToolSelect(toolType);
      }
    },
    [onSurgeryToolSelect]
  );

  const handleDataFusionViewToggle = useCallback(() => {
    if (sharedToolsState.dataFusion.onDataFusionToggle) {
      sharedToolsState.dataFusion.onDataFusionToggle();
    }
  }, [sharedToolsState.dataFusion]);

  const handle3dToggle = useCallback(() => {
    if (checkToolsState.threeDView.onView3dToggle) {
      checkToolsState.threeDView.onView3dToggle();
    }
  }, [checkToolsState.threeDView]);

  const handleRadioDensityToggle = useCallback(() => {
    if (checkToolsState.radioDensity.onRadioDensityToggle) {
      checkToolsState.radioDensity.onRadioDensityToggle();
    }
  }, [checkToolsState.radioDensity]);

  useEffect(() => {
    if (disabled && activeSubToolPopup) {
      closePopup();
    }
  }, [disabled, activeSubToolPopup, closePopup]);

  useEffect(() => {
    const handleClickOutside = event => {
      const shouldClosePopup = () =>
        activeSubToolPopup &&
        popupRef.current &&
        !popupRef.current.contains(event.target);

      if (shouldClosePopup()) {
        closePopup();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [activeSubToolPopup, closePopup]);

  const renderPopup = () => {
    const popupContent = {
      [POPUP_TYPES.DISTANCE]: (
        <DistanceTools
          selectedTool={selectedTool}
          onToolSelect={handleSubToolSelect}
          title={POPUP_TYPES.DISTANCE}
        />
      ),
      [POPUP_TYPES.ANGLE]: (
        <AngleTools
          selectedTool={selectedTool}
          onToolSelect={handleSubToolSelect}
          title={POPUP_TYPES.ANGLE}
        />
      ),
      [POPUP_TYPES.ADVANCED_MEASUREMENTS]: (
        <PredefinedTools
          selectedTool={selectedTool}
          onToolSelect={handleSubToolSelect}
          title={POPUP_TYPES.ADVANCED_MEASUREMENTS}
        />
      ),
      [POPUP_CONTENT_TYPES.ADD_VERTEBRA]: <AddVertebra onClose={closePopup} />,
      [POPUP_CONTENT_TYPES.REMOVE_VERTEBRA]: (
        <RemoveVertebra onClose={closePopup} />
      ),
      [POPUP_CONTENT_TYPES.ADD_LANDMARK]: <AddLandmark onClose={closePopup} />,
      [POPUP_CONTENT_TYPES.REMOVE_LANDMARK]: (
        <RemoveLandmark onClose={closePopup} />
      ),
    };

    return (
      <div
        ref={popupRef}
        className="tool-pop-up-container"
        style={{
          position: "fixed",
          top: `${popupPosition.top}px`,
          left: `${popupPosition.left}px`,
          transform: "translateY(-50%)",
        }}
      >
        <ToolPopUp
          title={activeSubToolPopup || ""}
          isOpen={!!activeSubToolPopup}
          position="left"
          onClose={
            activeSubToolPopup === POPUP_TYPES.DISTANCE ||
            activeSubToolPopup === POPUP_TYPES.ANGLE ||
            activeSubToolPopup === POPUP_TYPES.ADVANCED_MEASUREMENTS
              ? closePopup
              : undefined
          }
        >
          {activeSubToolPopup && popupContent[activeSubToolPopup]}
        </ToolPopUp>
      </div>
    );
  };

  const renderTools = () => {
    const canMeasure = checkToolsState.measurementsState.canMeasure;

    const distanceTool = (
      <ToolToggle
        ref={distanceButtonRef}
        icon={IconsEnum.DISTANCEGENERIC}
        shape={ToolShape.SQUARE}
        onClick={() => openPopup(POPUP_TYPES.DISTANCE, distanceButtonRef)}
        className="no-drag"
        text={t("410")}
        disabled={disabled || !canMeasure}
      />
    );

    const angleTool = (
      <ToolToggle
        ref={angleButtonRef}
        icon={IconsEnum.ANGLE}
        shape={ToolShape.SQUARE}
        onClick={() => openPopup(POPUP_TYPES.ANGLE, angleButtonRef)}
        className="no-drag"
        text={t("411")}
        disabled={disabled || !canMeasure}
      />
    );

    const advancedMeasurementsTool = (
      <ToolToggle
        ref={advancedButtonRef}
        icon={IconsEnum.MEASUREMENTS}
        shape={ToolShape.SQUARE}
        onClick={() =>
          openPopup(POPUP_TYPES.ADVANCED_MEASUREMENTS, advancedButtonRef)
        }
        className="no-drag"
        text={t("412")}
        disabled={disabled || !canMeasure}
      />
    );

    const advancedTools = (
      <>
        <ToolToggle
          isSelected={checkToolsState.radioDensity.isRadioDensityActive}
          onClick={handleRadioDensityToggle}
          shape={ToolShape.SQUARE}
          text={t("414")}
          icon={IconsEnum.RADIODENSITY}
          disabled={disabled || !checkToolsState.radioDensity.hasRadioDensity}
        />
        <ToolToggle
          isSelected={checkToolsState.threeDView.is3dViewActive}
          onClick={handle3dToggle}
          shape={ToolShape.SQUARE}
          text={t("415")}
          icon={IconsEnum.THREE_D}
          disabled={disabled || !checkToolsState.threeDView.has3dInstances}
        />
        <ToolToggle
          icon={IconsEnum.DATAFUSION}
          isSelected={sharedToolsState.dataFusion.isDataFusionActive}
          onClick={handleDataFusionViewToggle}
          shape={ToolShape.SQUARE}
          text={t("413")}
          disabled={disabled || !sharedToolsState.dataFusion.hasDataFusion}
        />
      </>
    );

    const fullMeasurementsTools = (
      <div className="check-tools-container">
        <div className="base-check-tools-container">
          {distanceTool}
          {angleTool}
          {advancedMeasurementsTool}
          {advancedTools}
        </div>
      </div>
    );

    const surgeryTools = (
      <>
        <div className="tools-container">
          <ToolToggle
            icon={IconsEnum.SPINE}
            onClick={() =>
              handleSurgeryToolClick(POPUP_CONTENT_TYPES.GLOBAL_ALIGNMENT)
            }
            isSelected={
              selectedSurgeryTool === POPUP_CONTENT_TYPES.GLOBAL_ALIGNMENT
            }
            className="no-drag"
            disabled={disabled || alignToolsState.globalAlignment}
            shape={ToolShape.SQUARE}
            text={t("424")}
          />
          <ToolToggle
            icon={IconsEnum.OSTEOTOMIES}
            onClick={() =>
              handleSurgeryToolClick(POPUP_CONTENT_TYPES.OSTEOTOMY)
            }
            isSelected={selectedSurgeryTool === POPUP_CONTENT_TYPES.OSTEOTOMY}
            className="no-drag"
            disabled={disabled || alignToolsState.osteotomy}
            shape={ToolShape.SQUARE}
            text={t("416")}
          />
          <ToolToggle
            icon={IconsEnum.SPONDY}
            onClick={() =>
              handleSurgeryToolClick(
                POPUP_CONTENT_TYPES.SEGMENTAL_TRANSFORMATION
              )
            }
            isSelected={
              selectedSurgeryTool ===
              POPUP_CONTENT_TYPES.SEGMENTAL_TRANSFORMATION
            }
            className="no-drag"
            disabled={disabled || alignToolsState.segmentalTransformation}
            shape={ToolShape.SQUARE}
            text={t("418")}
          />

          <ToolToggle
            icon={IconsEnum.CAGES}
            onClick={() => handleSurgeryToolClick(POPUP_CONTENT_TYPES.CAGES)}
            isSelected={selectedSurgeryTool === POPUP_CONTENT_TYPES.CAGES}
            className="no-drag"
            disabled={disabled || alignToolsState.cages}
            shape={ToolShape.SQUARE}
            text={t("417")}
          />
          <ToolToggle
            icon={IconsEnum.RODS}
            onClick={() => handleSurgeryToolClick(POPUP_CONTENT_TYPES.FUSION)}
            isSelected={selectedSurgeryTool === POPUP_CONTENT_TYPES.FUSION}
            className="no-drag"
            disabled={disabled || alignToolsState.fusion}
            shape={ToolShape.SQUARE}
            text={t("419")}
          />
        </div>
      </>
    );

    const validationTools = (
      <>
        {currentValidationRegion === MacroRegions.SPINE ? (
          <>
            <ToolToggle
              ref={addVertebraButtonRef}
              icon={IconsEnum.ADDVERTEBRA}
              onClick={() =>
                openPopup(
                  POPUP_CONTENT_TYPES.ADD_VERTEBRA,
                  addVertebraButtonRef
                )
              }
              className="no-drag"
              shape={ToolShape.SQUARE}
              text={t("445")}
              disabled={disabled}
            />
            <ToolToggle
              ref={removeVertebraButtonRef}
              icon={IconsEnum.REMOVEVERTEBRA}
              onClick={() =>
                openPopup(
                  POPUP_CONTENT_TYPES.REMOVE_VERTEBRA,
                  removeVertebraButtonRef
                )
              }
              className="no-drag"
              shape={ToolShape.SQUARE}
              text={t("446")}
              disabled={disabled}
            />
          </>
        ) : currentValidationRegion === MacroRegions.LOWER_BODY ? (
          <>
            <ToolToggle
              ref={addLandmarkButtonRef}
              icon={IconsEnum.ADDLANDMARK}
              onClick={() =>
                openPopup(
                  POPUP_CONTENT_TYPES.ADD_LANDMARK,
                  addLandmarkButtonRef
                )
              }
              className="no-drag"
              shape={ToolShape.SQUARE}
              text={t("447")}
              disabled={disabled}
            />
            <ToolToggle
              ref={removeLandmarkButtonRef}
              icon={IconsEnum.DELETE}
              onClick={() =>
                openPopup(
                  POPUP_CONTENT_TYPES.REMOVE_LANDMARK,
                  removeLandmarkButtonRef
                )
              }
              className="no-drag"
              shape={ToolShape.SQUARE}
              text={t("448")}
              disabled={disabled}
            />
          </>
        ) : null}
      </>
    );

    switch (step) {
      case ROUTE_TYPES.CHECK:
        return <div className="tools-container">{fullMeasurementsTools}</div>;
      case ROUTE_TYPES.VALIDATION:
        return <div className="tools-container">{validationTools}</div>;
      case ROUTE_TYPES.ALIGN:
        return <div className="tools-container">{surgeryTools}</div>;
      default:
        return <div className="tools-container">{}</div>;
    }
  };

  return (
    <div>
      {renderTools()}
      {renderPopup()}
    </div>
  );
}

export default ToolBar;
